import { client } from './generated/client.gen';
import { ApiError, toApiError } from './errors';

/**
 * The one place in the application that knows HTTP exists.
 *
 * Everything above this file calls generated functions and gets data or an
 * {@link ApiError}; a lint rule makes that not a convention but a fact (see
 * `eslint.config.js`). The point is not tidiness: the ERP partner in #219 is
 * promised that the API is the whole product, and the way to keep that promise
 * honest is for our own frontend to be under the same constraint.
 */

/**
 * Where requests go. Empty in development, so the browser talks to the Vite
 * dev server's own origin and the proxy forwards `/api` to uvicorn -- one
 * origin, no CORS, nothing about the backend baked into the bundle.
 */
const baseUrl: string = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * How the transport reaches the session, without importing it.
 *
 * The dependency runs one way -- the session layer registers itself here -- so
 * this file stays a transport concern and cannot grow opinions about sessions,
 * routing or roles.
 */
export interface ApiClientHooks {
  /** The bearer token to present, or undefined when nobody is signed in. */
  getAccessToken: () => string | undefined;
  /** Called when the server rejects the token we presented. */
  onUnauthenticated: () => void;
}

const noHooks: ApiClientHooks = {
  getAccessToken: () => undefined,
  onUnauthenticated: () => {},
};

let hooks: ApiClientHooks = noHooks;

export function configureApiClient(next: ApiClientHooks): void {
  hooks = next;
}

client.setConfig({
  baseUrl,
  // The token is read per request rather than captured, so signing out takes
  // effect on the very next call instead of on the next page load.
  auth: () => hooks.getAccessToken(),
});

/**
 * A 401 means the token we hold is no longer good -- expired, revoked, or the
 * account was deactivated, which the backend re-checks on every request. The
 * session is dropped here, at the moment the server says so, which is what
 * stops a stale session from turning into a broken screen or a spinner that
 * never resolves.
 *
 * Sign-in itself answers 401 for a wrong password. There is no session to drop
 * in that case, so the hook is a no-op and the login form handles it.
 */
client.interceptors.response.use((response) => {
  if (response.status === 401) {
    hooks.onUnauthenticated();
  }
  return response;
});

/**
 * Spread into the three calls that authenticate on the session cookie rather
 * than on a header: sign-in, renewal and sign-out.
 *
 * `credentials: 'include'` is what makes the browser attach the cookie and
 * honour the `Set-Cookie` that comes back, and it is deliberately *not* the
 * default for every request. Two reasons. Nothing else in the API reads a
 * cookie -- a bearer token is a header the page already holds -- and switching
 * it on globally would demand `Access-Control-Allow-Credentials` on every
 * cross-origin response, which the backend only grants to these three paths and
 * only for a configured origin. See `app/middleware/cors.py` for why the grant
 * stops there.
 *
 * It lives here rather than at the call sites because `credentials` is fetch's
 * vocabulary, and this file is the only one allowed to speak it.
 */
export const WITH_SESSION_COOKIE = { credentials: 'include' } as const;

/** The two fields of a generated result the facade cares about. */
type ApiResult = {
  data?: unknown;
  error?: unknown;
  response?: Response | undefined;
};

/**
 * Unwrap a generated call: the data, or a thrown {@link ApiError}.
 *
 * The generated client returns failures rather than throwing them, which is a
 * reasonable default for a library and the wrong default for screens. React
 * Query, error boundaries and `try`/`catch` all speak exceptions, and a result
 * object that nobody remembered to check renders as `undefined`.
 */
export async function call<TResult extends ApiResult>(
  pending: Promise<TResult>,
): Promise<NonNullable<TResult['data']>> {
  const result = await pending;
  if (result.error !== undefined || result.data === undefined) {
    throw toApiError(result.error, result.response);
  }
  return result.data as NonNullable<TResult['data']>;
}

/** A file the server sent as an attachment: its bytes, and the name it gave them. */
export interface Attachment {
  readonly blob: Blob;
  readonly filename: string | undefined;
}

/**
 * Unwrap a call made with `parseAs: 'blob'`, keeping the name with the bytes.
 *
 * {@link call} hands back the body alone, which is right for JSON and wrong for
 * a download: what a file is saved as lives in `Content-Disposition`, and that
 * is the server's answer rather than something a screen should be composing
 * out of whichever fields it happens to be holding. Two screens inventing two
 * names for one file is how an operations mailbox ends up with attachments
 * nobody can match to a month. Reading a header is fetch's vocabulary, so it
 * is read here and nowhere else.
 *
 * `filename` is `undefined` when the server did not name the file. The caller
 * decides what to do about that, because the sensible fallback is the screen's
 * to know.
 */
export async function callAttachment<TResult extends ApiResult>(
  pending: Promise<TResult>,
): Promise<Attachment> {
  const result = await pending;
  if (result.error !== undefined || result.data === undefined) {
    throw toApiError(result.error, result.response);
  }
  return { blob: result.data as Blob, filename: attachmentName(result.response) };
}

// `filename="x.csv"`, `filename=x.csv` and RFC 5987's `filename*=UTF-8''x.csv`
// -- the three shapes the header is written in.
const FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

function attachmentName(response: Response | undefined): string | undefined {
  const disposition = response?.headers.get('Content-Disposition');
  if (disposition == null) {
    return undefined;
  }
  const name = FILENAME.exec(disposition)?.[1];
  if (name === undefined) {
    return undefined;
  }
  // A name the server percent-encoded, decoded once; a name it did not, left
  // alone rather than rejected over a stray `%`.
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

export { ApiError };
